import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Layout from '../PageLayout/Layout';
import './ContactPage.css';

export default function ContactForm() {
  const [formData, setFormData] = useState({
    name: '',
    companyName: '',
    email: '',
    phone: '',
    message: '',
  });
  const [status, setStatus] = useState('idle'); // 'idle' | 'submitting' | 'success' | 'error'
  const [captchaError, setCaptchaError] = useState(null);

  // Fallback CAPTCHA components (when no Google site key)
  const [simpleCaptcha, setSimpleCaptcha] = useState('');
  const [simpleCaptchaInput, setSimpleCaptchaInput] = useState('');
  const [simpleHuman, setSimpleHuman] = useState(false);

  // 🔧 For LOCAL TESTING: hardcode backend URL
  // When deployment is ready, you can revert to:
  // const API_URL = process.env.REACT_APP_API_URL;
  const API_URL = process.env.REACT_APP_API_URL;
  const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const generateSimpleCaptcha = () => {
    const alphabet = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
    let code = '';
    for (let i = 0; i < 6; i += 1) {
      code += alphabet[Math.floor(Math.random() * alphabet.length)];
    }
    setSimpleCaptcha(code);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    let recaptchaToken = null;
    try {
      if (RECAPTCHA_SITE_KEY && window.grecaptcha) {
        recaptchaToken = window.grecaptcha.getResponse();
        if (!recaptchaToken) {
          setCaptchaError('Please confirm you are not a robot.');
          setStatus('error');
          return;
        }
        setCaptchaError(null);
      }
    } catch (e1) {
      console.warn('⚠️ reCAPTCHA not available; continuing without token');
    }
    if (!RECAPTCHA_SITE_KEY) {
      if (!simpleHuman) {
        setCaptchaError('Please confirm you are not a robot.');
        setStatus('error');
        return;
      }
      if (
        !simpleCaptchaInput ||
        simpleCaptchaInput.trim().toUpperCase() !== simpleCaptcha.toUpperCase()
      ) {
        setCaptchaError('Captcha code does not match.');
        setStatus('error');
        return;
      }
      setCaptchaError(null);
    }
    setStatus('submitting');
    try {
      const endpoint = API_URL ? `${API_URL}/api/contact` : '/api/contact';
  
      console.log("Submitting to:", endpoint);
  
      const payload = {
        fullName: formData.name,
        companyName: formData.companyName,
        email: formData.email,
        phone: formData.phone,
        message: formData.message,
        recaptchaToken,
      };
  
      await axios.post(endpoint, payload, {
        headers: {
          "Content-Type": "application/json"
        }
      });
  
      setStatus('success');
  
      setFormData({
        name: '',
        companyName: '',
        email: '',
        phone: '',
        message: '',
      });
  
      if (window.grecaptcha) {
        try {
          window.grecaptcha.reset();
        } catch {}
      }
  
      setSimpleCaptchaInput('');
      setSimpleHuman(false);
  
      if (!RECAPTCHA_SITE_KEY) generateSimpleCaptcha();
  
    } catch (err) {
      console.error('❌ Contact form submit error:', err);
      setStatus('error');
    }
  };

  useEffect(() => {
    if (status === 'success') {
      const timer = setTimeout(() => setStatus('idle'), 4000);
      return () => clearTimeout(timer);
    }
  }, [status]);

  useEffect(() => {
    // Initialize fallback CAPTCHA when no Google site key
    if (!RECAPTCHA_SITE_KEY) generateSimpleCaptcha();
  }, [RECAPTCHA_SITE_KEY]);

  return (
    <Layout flushMobile>
      <div className="contact-page internal-hero-container">
        <div className="flex flex-col md:flex-row items-start justify-between py-2 md:py-8 gap-6 md:gap-10">
          {/* Left Side */}
          <div className="md:w-1/2 max-w-xl text-left">
            <div className="internal-hero-badge mb-10 md:mb-6" style={{ marginTop: 0, display: 'inline-flex' }}>
              <img src="/icons/contact.svg" alt="Contact Icon" className="w-4 h-4 mr-2" />
              Contact
            </div>
            <h2 className="text-3xl md:text-[38px] font-bold text-gray-900 leading-tight md:leading-snug mb-4">
              Connect with Highwatch AI. The trusted intelligence layer for your enterprise.
            </h2>
            <p className="text-[15px] leading-relaxed" style={{ color: 'var(--corelink-text-secondary)' }}>
              Whether you’re exploring Highwatch, seeking integration guidance, or discussing pilot deployment, our team is here to support you.
              <br />
              Every conversation begins with trust, compliance, and measurable impact.
            </p>
          </div>

          {/* Right Side Form */}
          <form onSubmit={handleSubmit} className="md:w-[480px] w-full bg-white rounded-2xl shadow-xl p-6 md:p-8 space-y-5 text-left">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 text-left">Full Name <span className="text-red-500">*</span></label>
              <input name="name" type="text" value={formData.name} onChange={handleChange} placeholder="Enter your full name" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-300 text-left" required />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 text-left">Company Name</label>
              <input name="companyName" type="text" value={formData.companyName} onChange={handleChange} placeholder="Enter your company name" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-300 text-left" />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 text-left">Business E-Mail Address <span className="text-red-500">*</span></label>
              <input name="email" type="email" value={formData.email} onChange={handleChange} placeholder="Enter your business e-mail address" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-300 text-left" required />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 text-left">Phone Number <span className="text-red-500">*</span></label>
              <input name="phone" type="tel" value={formData.phone} onChange={handleChange} placeholder="Enter your phone number" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-300 text-left" required />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 text-left">Message or Inquiry</label>
              <textarea name="message" value={formData.message} onChange={handleChange} placeholder="Message or inquiry" rows={4} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-300 text-left" />
            </div>

            {/* Captcha section */}
            {RECAPTCHA_SITE_KEY ? (
              <div className="space-y-2">
                <div className="g-recaptcha" data-sitekey={RECAPTCHA_SITE_KEY} id="recaptcha-container" />
                {captchaError && <p className="text-sm text-red-600">{captchaError}</p>}
              </div>
            ) : (
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700 text-left">Captcha</label>
                <div className="flex items-center gap-3">
                  <div className="px-3 py-2 rounded bg-gray-100 border font-mono tracking-widest select-none" aria-label="Captcha code">{simpleCaptcha}</div>
                  <button type="button" className="text-sm px-2 py-1 border rounded" onClick={generateSimpleCaptcha}>Refresh</button>
                </div>
                <input type="text" value={simpleCaptchaInput} onChange={(e) => setSimpleCaptchaInput(e.target.value)} placeholder="Enter the code shown above" className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-indigo-300 text-left" aria-label="Enter captcha code" />
                <div className="flex items-center gap-2">
                  <input id="simple-human" type="checkbox" checked={simpleHuman} onChange={(e) => setSimpleHuman(e.target.checked)} className="h-4 w-4" />
                  <label htmlFor="simple-human" className="text-sm text-gray-700 select-none">I’m not a robot</label>
                </div>
                {captchaError && <p className="text-sm text-red-600">{captchaError}</p>}
              </div>
            )}

            <button type="submit" className="w-full font-medium py-3 rounded-lg shadow-md transition border" style={{ backgroundColor: 'var(--corelink-navy)', color: 'var(--corelink-text-primary)', borderColor: 'transparent' }} disabled={status === 'submitting'}>
              {status === 'submitting' ? 'Submitting...' : 'Connect with Our Team'}
            </button>

            <p className="text-xs" style={{ color: 'var(--corelink-text-secondary)' }}>
              Your data is processed securely in compliance with SOC 2 and GDPR standards.
            </p>
          </form>
        </div>

        <div aria-live="polite" className="mt-4 w-full">
          {status === 'success' && (
            <div className="rounded-md px-4 py-3 flex items-center gap-3 shadow-md animate-fade-in" style={{ backgroundColor: '#2DD4BF', color: '#0C1C32' }}>
              <span aria-hidden="true" className="inline-block w-2 h-2 rounded-full" style={{ backgroundColor: '#0C1C32' }} />
              <span>Message sent successfully. Our team will contact you within 24 hours.</span>
            </div>
          )}
          {status === 'error' && (
            <div className="rounded-md px-4 py-3 flex items-center gap-3 shadow-md" style={{ backgroundColor: '#DC2626', color: '#FFFFFF' }}>
              <span aria-hidden="true" className="inline-block w-2 h-2 rounded-full bg-white" />
              <span>Message not sent. Please check your connection or try again.</span>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
